/**
 * Camera-fit decisions for the map panel. Nothing here imports mapbox-gl, so
 * these run in the node test project, which gives mapbox-gl no browser
 * environment to load into.
 */

/** Geographic extent of a set of coordinates, in degrees. */
export interface GeoBounds {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

/** Bounds and options for a `mapboxgl.Map.fitBounds` call. */
export interface CameraFitCommand {
  corners: [[number, number], [number, number]];
  options: {
    padding: number;
    maxZoom: number;
    animate: boolean;
  };
}

/** Screen-pixel gap between the fitted bounds and the panel edges. */
const FIT_PADDING_PX = 40;

/**
 * Zoom cap for bounds with no extent at all: a path holding a single
 * coordinate, or several identical ones. Fitting those to full zoom would
 * frame one point at millimetre resolution with nothing around it.
 */
const NO_EXTENT_FIT_MAX_ZOOM = 17;

/**
 * The zoom the panel's GeoJSON sources are indexed to, and the ceiling
 * geojson-vt accepts for a source's `maxzoom`.
 *
 * A source indexed lower rounds every coordinate onto that zoom's tile grid
 * before the renderer sees it: at mapbox's default of 18 a grid cell is about
 * 1.5cm on the ground, which is what left a 3cm GPS track drawn as a point.
 * At 24 a cell is 0.231mm, so a 3.6cm track is carried as about 154 distinct
 * positions rather than two.
 */
export const SOURCE_MAX_ZOOM = 24;

/** Grid units across a vector tile, and the pixels mapbox draws it into. */
const TILE_GRID_UNITS = 8192;
const TILE_PX = 512;

/**
 * How far the camera may zoom, and the cap the fit runs to for any bounds with
 * extent.
 *
 * Mapbox enforces no ceiling here -- its transform defaults to 25.5 and the
 * setter does not clamp -- so what bounds this is the precision of what the
 * sources stored. One SOURCE_MAX_ZOOM grid unit covers exactly one screen
 * pixel at the zoom below; past that the camera magnifies the tile grid rather
 * than the track, and a path starts to read as stair-steps. At 28 a 3.6cm
 * track draws about 207px across, which is the finest this pipeline can
 * honestly show.
 *
 * Derived rather than written as 28 so that retuning SOURCE_MAX_ZOOM carries
 * the camera with it; left as a literal, lowering the source zoom would return
 * the stair-steps this exists to prevent.
 *
 * Fitting every path with extent to this zoom means a path that only jitters,
 * such as a stationary robot's GPS noise, fills the panel where it used to
 * open at zoom 17 with its surroundings in view. The map's scale control is
 * what distinguishes the two.
 */
export const CAMERA_MAX_ZOOM =
  SOURCE_MAX_ZOOM + Math.log2(TILE_GRID_UNITS / TILE_PX);

/**
 * Corners run [[west, south], [east, north]], the order mapbox reads as a
 * LngLatBoundsLike. West is the numeric minimum longitude, not the westward
 * end of the shorter arc, so a path spanning the antimeridian is framed the
 * long way around rather than across it.
 */
function cameraFit(
  bounds: GeoBounds,
  { animate }: { animate: boolean },
): CameraFitCommand {
  // Any difference counts, down to the last bit of a double. A extent finer
  // than one SOURCE_MAX_ZOOM grid cell (0.231mm) still takes the full cap and
  // so can still frame as a point -- deliberate, because the alternative is
  // guessing which sub-millimetre spreads are real movement and which are a
  // stationary receiver's noise, and the scale bar says which one is on
  // screen either way.
  const hasExtent =
    bounds.minLat !== bounds.maxLat || bounds.minLon !== bounds.maxLon;

  return {
    corners: [
      [bounds.minLon, bounds.minLat],
      [bounds.maxLon, bounds.maxLat],
    ],
    options: {
      padding: FIT_PADDING_PX,
      maxZoom: hasExtent ? CAMERA_MAX_ZOOM : NO_EXTENT_FIT_MAX_ZOOM,
      animate,
    },
  };
}

/**
 * The smallest bounds enclosing every defined entry, or undefined when there
 * are none.
 */
export function mergeGeoBounds(
  boundsList: ReadonlyArray<GeoBounds | undefined>,
): GeoBounds | undefined {
  let merged: GeoBounds | undefined;

  for (const bounds of boundsList) {
    if (bounds === undefined) {
      continue;
    }

    if (merged === undefined) {
      merged = { ...bounds };
      continue;
    }

    merged.minLat = Math.min(merged.minLat, bounds.minLat);
    merged.maxLat = Math.max(merged.maxLat, bounds.maxLat);
    merged.minLon = Math.min(merged.minLon, bounds.minLon);
    merged.maxLon = Math.max(merged.maxLon, bounds.maxLon);
  }

  return merged;
}

/**
 * Fit command covering the merged bounds of all visible paths, or undefined
 * when no visible path has coordinates yet (leave the camera alone).
 * Not animated: this runs after every streamed data chunk, and animated fits
 * would queue competing camera transitions.
 */
export function cameraFitForVisiblePaths(
  paths: ReadonlyArray<{ id: string; visible: boolean }>,
  getPathBounds: (pathId: string) => GeoBounds | undefined,
): CameraFitCommand | undefined {
  const merged = mergeGeoBounds(
    paths.filter((path) => path.visible).map((path) => getPathBounds(path.id)),
  );

  if (merged === undefined) {
    return undefined;
  }

  return cameraFit(merged, { animate: false });
}

/**
 * Fit command covering a single path's bounds, or undefined when the path has
 * no coordinates yet. Animated: this backs one-shot actions like the legend's
 * click-to-center, so no stream of later fits queues up behind the transition.
 */
export function cameraFitForPathBounds(
  bounds: GeoBounds | undefined,
): CameraFitCommand | undefined {
  if (bounds === undefined) {
    return undefined;
  }

  return cameraFit(bounds, { animate: true });
}
